import {computed, ref} from 'vue';
import {defineStore, storeToRefs} from 'pinia';
import {useCookie, useLogger} from '#imports';
import {useDomainChecker} from '~/composables/useDomainChecker';
import {useAppraisalStore} from '~/features/appraisals/store/appraisalStore';
import {useRecommendationsStore} from '~/features/recommendations/store/recommendations.store';
import {Configuration, type ProfileTranslation, ProfileTranslationApi, type Skill} from '~/openapi';
import {useProfileStore} from '~/store/profileStore';
import {useSkillSetStore} from '~/store/skill-set/skillSetStore';
import type {Appraisal} from '~/types/domain/Appraisals';
import {type Appraisals} from '~/types/domain/Appraisals';
import type {Certification} from '~/types/domain/Certifications';
import {type Certifications} from '~/types/domain/Certifications';
import type {Education} from '~/types/domain/Educations';
import {type Educations} from '~/types/domain/Educations';
import type {Experience} from '~/types/domain/Experiences';
import {type Experiences} from '~/types/domain/Experiences';
import {type Profile} from '~/types/domain/Profile';
import type {Recommendation} from '~/types/domain/Recommendations';
import {type Recommendations} from '~/types/domain/Recommendations';
import {SectionsVisibilityType} from '~/types/domain/SectionsVisibility';
import {useSectionsVisibilityStore} from '../sectionsVisibility';

type TranslatableContent = {
    profile?: Profile;
    topSkills?: Skill[];
    selectedSkills?: Skill[];
    experiences?: Experiences;
    educations?: Educations;
    certifications?: Certifications;
    appraisals?: Appraisals;
    recommendations?: Recommendations;
};

export const useAsyncTranslationStore = defineStore('async-translations', () => {
    const logger = useLogger();
    const {isMaltDotComWithoutPrefix} = useDomainChecker();
    const apiConfiguration = new Configuration({basePath: ''});
    const profileTranslationApi = new ProfileTranslationApi(apiConfiguration);
    const profileStore = useProfileStore();
    const {setCertifications, setEducations, setExperiences, setProfile} = profileStore;
    const {certifications, educations, experiences, profile} = storeToRefs(profileStore);
    const appraisalStore = useAppraisalStore();
    const {setAppraisals} = appraisalStore;
    const {appraisals} = storeToRefs(appraisalStore);
    const recommendationsStore = useRecommendationsStore();
    const {setRecommendations} = recommendationsStore;
    const {recommendations} = storeToRefs(recommendationsStore);
    const skillSetStore = useSkillSetStore();
    const {setProfileSkillSet} = skillSetStore;
    const {profileTopSkills, profileSelectedSkills} = storeToRefs(skillSetStore);

    const profileTranslation = ref<ProfileTranslation>();
    const originalValues = ref<TranslatableContent>();
    const isTranslated = ref<boolean>(false);
    const isProfileTranslationActiveForUser = useCookie('is-profile-translation-active', {
        default: () => true,
    });

    const {sectionVisibility} = storeToRefs(useSectionsVisibilityStore());
    const isFeatureAvailable = computed(() => isMaltDotComWithoutPrefix() && sectionVisibility.value[SectionsVisibilityType.ASYNC_TRANSLATION]);

    const hasTranslation = computed(() => profileTranslation.value !== undefined);
    const translatedValues = computed<TranslatableContent>(() => ({
        profile: Object.assign({}, originalValues.value?.profile, {
            headline: computeHeadline(),
            description: computeDescription(),
        }),
        topSkills: computeTopSkills(),
        selectedSkills: computeRemainingSkills(),
        experiences: Object.assign({}, originalValues.value?.experiences, {
            items: computeExperiences(),
        }),
        educations: Object.assign({}, originalValues.value?.educations, {
            items: computeEducations(),
        }),
        certifications: Object.assign({}, originalValues.value?.certifications, {
            items: computeCertifications(),
        }),
        appraisals: Object.assign({}, originalValues.value?.appraisals, {
            appraisals: computeAppraisals(),
        }),
        recommendations: Object.assign({}, originalValues.value?.recommendations, {
            recommendations: computeRecommendations(),
        }),
    }));

    async function initTranslationIfNeeded(isTranslationByDefaultEnabled: boolean): Promise<void> {
        // Save initial values + fetch translations
        if (isFeatureAvailable.value) {
            saveOriginalValues();
            await fetchTranslations();
        }

        // Then, translate profile by default if needed (enabled + requested by user) & available
        if (isTranslationByDefaultEnabled && isProfileTranslationActiveForUser.value && hasTranslation.value) {
            translate();
        }
    }

    function saveOriginalValues() {
        originalValues.value = {
            profile: profile.value,
            topSkills: profileTopSkills.value,
            selectedSkills: profileSelectedSkills.value,
            experiences: experiences.value,
            educations: educations.value,
            certifications: certifications.value,
            appraisals: appraisals.value,
            recommendations: recommendations.value,
        };
    }

    async function fetchTranslations(): Promise<void> {
        const profileId = profile.value!.profileId;
        try {
            profileTranslation.value = await profileTranslationApi.getProfileTranslation({
                profileId,
                language: 'en',
            });
        } catch (e) {
            logger.info(`Can not find available translation for profileId: ${profileId}`, e);
            profileTranslation.value = undefined;
        }
    }

    function restore() {
        isTranslated.value = false;
        isProfileTranslationActiveForUser.value = false;
        switchValues(originalValues.value);
    }

    function translate() {
        if (!hasTranslation.value) {
            logger.warn('Try to translate profile with no available translation');
            return;
        }
        isTranslated.value = true;
        isProfileTranslationActiveForUser.value = true;
        switchValues(translatedValues.value);
    }

    function switchValues(values: TranslatableContent | undefined) {
        setProfile(values?.profile);
        setProfileSkillSet(values?.selectedSkills, values?.topSkills);
        setExperiences(values?.experiences);
        setEducations(values?.educations);
        setCertifications(values?.certifications);
        setAppraisals(values?.appraisals);
        setRecommendations(values?.recommendations);
    }

    function computeHeadline() {
        return profileTranslation.value?.headline ?? originalValues.value?.profile?.headline;
    }

    function computeDescription() {
        return profileTranslation.value?.description ?? originalValues.value?.profile?.description;
    }

    function computeTopSkills(): Skill[] {
        const topSkillTranslations = profileTranslation.value?.topSkills ?? [];
        const topSkillTranslationsById = storeById(topSkillTranslations, (skill) => skill.id);
        return computeTranslations(
            originalValues.value?.topSkills ?? [],
            topSkillTranslationsById,
            (skill) => skill.id,
            (originalSkill, skillTranslation) => ({
                label: skillTranslation.name,
            }),
        );
    }

    function computeRemainingSkills(): Skill[] {
        const remainingSkillTranslations = profileTranslation.value?.remainingSkills ?? [];
        const remainingSkillTranslationsById = storeById(remainingSkillTranslations, (skill) => skill.id);
        return computeTranslations(
            originalValues.value?.selectedSkills ?? [],
            remainingSkillTranslationsById,
            (skill) => skill.id,
            (originalSkill, skillTranslation) => ({
                label: skillTranslation.name,
            }),
        );
    }

    function computeExperiences(): Experience[] {
        const experienceTranslations = profileTranslation.value?.experiences ?? [];
        const experienceTranslationsById = storeById(experienceTranslations, (exp) => exp.id);
        return computeTranslations(
            originalValues.value?.experiences?.items ?? [],
            experienceTranslationsById,
            (exp) => exp.id,
            (originalExperience, experienceTranslation) => {
                const translationSkillsById = storeById(experienceTranslation.skills ?? [], (skill) => skill.id);
                const translatedSkills = computeTranslations(
                    originalExperience.skills ?? [],
                    translationSkillsById,
                    (item) => item.id,
                    (originalSkill, skillTranslation) => ({
                        label: skillTranslation.name,
                    }),
                );
                return {
                    title: experienceTranslation.title,
                    description: experienceTranslation.description,
                    skills: translatedSkills,
                };
            },
        );
    }

    function computeEducations(): Education[] {
        const educationTranslations = profileTranslation.value?.educations ?? [];
        const educationTranslationsById = storeById(educationTranslations, (education) => education.id);
        return computeTranslations(
            originalValues.value?.educations?.items ?? [],
            educationTranslationsById,
            (education) => education.description + '-' + education.title + '-' + education.graduationYear, // hack: generate id from values
            (originalEducation, educationTranslation) => ({
                title: educationTranslation.title,
                description: educationTranslation.description,
            }),
        );
    }

    function computeCertifications(): Certification[] {
        const certificationTranslations = profileTranslation.value?.certifications ?? [];
        const certificationTranslationsById = storeById(certificationTranslations, (certification) => certification.id);
        return computeTranslations(
            originalValues.value?.certifications?.items ?? [],
            certificationTranslationsById,
            (certification) => certification.id,
            (originalCertification, certificationTranslation) => {
                const skillTranslationsById = storeById(certificationTranslation.skills ?? [], (skill) => skill.id);
                return {
                    title: certificationTranslation.title,
                    organization: certificationTranslation.organization,
                    skills: originalCertification.skills.map((skill) => skillTranslationsById[skill]?.name ?? skill),
                };
            },
        );
    }

    function computeAppraisals(): Appraisal[] {
        const appraisalTranslations = profileTranslation.value?.appraisals ?? [];
        const appraisalTranslationsById = storeById(appraisalTranslations, (appraisal) => appraisal.id);
        return computeTranslations(
            originalValues.value?.appraisals?.appraisals ?? [],
            appraisalTranslationsById,
            (appraisal) => appraisal.appraisalId,
            (originalAppraisal, appraisalTranslation) => ({
                comment: appraisalTranslation.comment,
            }),
        );
    }

    function computeRecommendations(): Recommendation[] {
        const recommendationTranslations = profileTranslation.value?.recommendations ?? [];
        const recommendationTranslationsByAuthorAccountId = storeById(recommendationTranslations, (recommendation) => recommendation.authorAccountId);
        return computeTranslations(
            originalValues.value?.recommendations?.recommendations ?? [],
            recommendationTranslationsByAuthorAccountId,
            (recommendation) => recommendation.accountId,
            (originalRecommendation, recommendationTranslation) => ({
                textContent: recommendationTranslation.message,
            }),
        );
    }

    function computeTranslations<T, U>(
        originalValues: T[],
        translationsById: Record<string, U>,
        getKey: (item: T) => string,
        translationOperation: (original: T, translated: U) => Partial<T>,
    ): T[] {
        return originalValues.map((original) => {
            const key = getKey(original);
            if (key in translationsById) {
                const translated = translationsById[key];
                return Object.assign({}, original, translationOperation(original, translated));
            } else {
                return original;
            }
        });
    }

    function storeById<T>(list: T[], getKey: (item: T) => string): Record<string, T> {
        return Object.fromEntries(list.map((item) => [getKey(item), item]));
    }

    return {
        initTranslationIfNeeded,
        fetchTranslations,
        translate,
        restore,
        originalValues,
        profileTranslation,
        hasTranslation,
        isFeatureAvailable,
        isTranslated,
        isProfileTranslationActiveForUser,
    };
});
